import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { TaskDrawer } from '../components/TaskDrawer';
import type { Priority, PriorityItem } from '../lib/types';
import { PriorityBadge, StatusDot } from '../components/Badges';
import { formatDate } from '../lib/dates';
import { DataTable } from '../components/DataTable';
import type { Column } from '../components/DataTable';

interface Props {
  data: { items: PriorityItem[]; withheld: string | null } | null;
  showProject?: boolean;
  /** Called after a task is edited in the drawer, so the list can refresh. */
  onChanged?: () => void | Promise<void>;
}

/**
 * The ranked open-work list. Shared by the company view and the cross-company view so a
 * priority reads identically wherever it is seen.
 */
/** Sorting by name would put "critical" after "low", which is worse than useless. */
const PRIORITY_RANK: Record<Priority, number> = { critical: 0, high: 1, medium: 2, low: 3, none: 4 };

export function PriorityTable({ data, showProject = false, onChanged }: Props) {
  // Before the early returns: a hook cannot run conditionally.
  const [open, setOpen] = useState<{ id: string; projectId: string } | null>(null);

  const columns: Column<PriorityItem>[] = useMemo(
    () => [
      {
        key: 'item',
        header: 'Item',
        width: '40%',
        value: (item) => `${item.key} ${item.title}`,
        filter: 'text',
        render: (item) => (
          <div className="row" style={{ gap: 8 }}>
            <span className="key">{item.key}</span>
            <span className="truncate" style={{ maxWidth: 380 }}>
              {item.title}
            </span>
            {item.blockedSince && <span className="badge badge-accent">blocked</span>}
          </div>
        ),
      },
      ...(showProject
        ? ([
            {
              key: 'project',
              header: 'Project',
              value: (item: PriorityItem) => item.project.name,
              filter: 'choice',
              render: (item: PriorityItem) => (
                /* Stops the row's own click, so the project cell still goes to the project. */
                <Link
                  to={`/projects/${item.project.id}`}
                  onClick={(event) => event.stopPropagation()}
                  className="muted"
                >
                  {item.project.name}
                </Link>
              ),
            },
          ] as Column<PriorityItem>[])
        : []),
      {
        key: 'priority',
        header: 'Priority',
        value: (item) => (item.priority === 'none' ? '' : item.priority),
        sortValue: (item) => PRIORITY_RANK[item.priority],
        filter: 'choice',
        render: (item) => <PriorityBadge priority={item.priority} />,
      },
      {
        key: 'status',
        header: 'Status',
        value: (item) => item.status?.label ?? '',
        filter: 'choice',
        render: (item) =>
          item.status ? <StatusDot color={item.status.color} label={item.status.label} /> : <span className="dim">—</span>,
      },
      {
        key: 'assignee',
        header: 'Assignee',
        value: (item) => (item.assignees.length === 0 ? 'Unassigned' : item.assignees.map((person) => person.name).join(', ')),
        filter: 'text',
        render: (item) => (
          <span className="muted">
            {item.assignees.length === 0 ? 'Unassigned' : item.assignees.map((person) => person.name).join(', ')}
          </span>
        ),
      },
      {
        key: 'due',
        header: 'Due',
        value: (item) => item.dueDate,
        filter: 'none',
        render: (item) => (
          <span style={item.overdue ? { color: 'var(--accent)', fontWeight: 600 } : undefined}>
            {item.dueDate ? formatDate(item.dueDate) : <span className="dim">—</span>}
          </span>
        ),
      },
    ],
    [showProject],
  );

  if (!data) return <div className="empty">Loading…</div>;
  if (data.withheld) return <div className="empty">{data.withheld}</div>;
  if (data.items.length === 0) {
    return (
      <div className="empty">
        <h3>Nothing open</h3>
        <p>Work items will appear here once boards are in use.</p>
      </div>
    );
  }

  return (
    <>
    {/*
      The whole row opens the task IN PLACE, over the list.
      It first navigated to that project's board, which is what Issues does — but a priority
      list is something you read down, and being thrown onto a board loses your place in it.
      The project cell is still how you go to the project.
    */}
    <DataTable
      tableKey={showProject ? 'priorities-all' : 'priorities'}
      exportName="priorities"
      columns={columns}
      rows={data.items}
      searchPlaceholder="Find in this list"
      onRowClick={(item) => setOpen({ id: item.id, projectId: item.project.id })}
    />

    {/* Over the list, not inside the scrolling table. */}
    {open && (
      <TaskDrawer
        itemId={open.id}
        projectId={open.projectId}
        onClose={() => setOpen(null)}
        onChanged={onChanged}
      />
    )}
    </>
  );
}
